/**
 * /marketplace/upcoming — Public list of planned breedings + expected
 * litters that have paid the listing fee. Featured items sort first.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { photoUrl } from "@/lib/utils-app";

const DOLLARS = (cents) => (cents == null ? "—" : `$${(cents / 100).toFixed(0)}`);

export default function MarketplaceUpcoming() {
  const [rows, setRows] = useState([]);
  const [breed, setBreed] = useState("");
  const [stage, setStage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (breed) params.breed = breed;
    if (stage) params.stage = stage;
    api
      .get("/public/upcoming-breedings", { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [breed, stage]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
            Marketplace · Upcoming Litters
          </p>
          <h1 className="font-spectral text-4xl tracking-tight">
            Planned breedings & expected litters
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Reserve a spot on a breeder's waitlist with a deposit paid directly to
            the breeder. Armstrong K9 Registry is a venue — every breeder publishes their own
            refund policy and contract, reviewed by their own counsel.
          </p>
        </div>

        <div className="flex gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] font-bold">
              Breed
            </label>
            <Input
              value={breed}
              onChange={(e) => setBreed(e.target.value)}
              placeholder="any breed"
              className="w-48"
              data-testid="upcoming-filter-breed"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] font-bold">
              Stage
            </label>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className="block rounded-md border border-[#E5E5E5] p-2 text-sm"
              data-testid="upcoming-filter-stage"
            >
              <option value="">All</option>
              <option value="planned">Planned</option>
              <option value="expecting">Expecting</option>
            </select>
          </div>
        </div>

        {loading && <p className="text-sm text-[#6B6B6B]">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-[#6B6B6B]">No upcoming litters published yet.</p>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => {
            const featured =
              r.is_featured ||
              (r.featured_until && new Date(r.featured_until) > new Date());
            const isElite = !!r.is_elite_kennel;
            const damPhoto = photoUrl(r.dam_snapshot?.photo_url);
            const sirePhoto = photoUrl(r.sire_snapshot?.photo_url);
            const hasAnyPhoto = !!(damPhoto || sirePhoto);
            // When only one parent has a photo, mirror it on both sides
            // so the card never looks half-empty — better than the
            // gray "no photo" rectangle.
            const leftPhoto = damPhoto || sirePhoto;
            const rightPhoto = sirePhoto || damPhoto;
            return (
              <Link
                key={r.id}
                to={`/marketplace/upcoming/${r.id}`}
                className={`relative block bg-white rounded-xl overflow-hidden transition ${
                  isElite
                    ? "border-2 border-[#C9A227]/60 shadow-[0_2px_10px_rgba(201,162,39,0.18)] hover:shadow-[0_4px_18px_rgba(201,162,39,0.28)]"
                    : "border border-[#EAEAEA] hover:border-ink"
                }`}
                data-testid={`upcoming-card-${r.id}`}
              >
                {isElite && (
                  <div
                    className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[0.18em] uppercase"
                    style={{
                      background: "linear-gradient(180deg, #2D2D2D 0%, #0E0E0E 55%, #1A1A1A 100%)",
                      color: "#E8C75E",
                      boxShadow:
                        "inset 0 1px 0 rgba(232,199,94,0.4), inset 0 -1px 0 rgba(0,0,0,0.6), 0 1px 2px rgba(14,14,14,0.18)",
                      textShadow: "0 1px 0 rgba(0,0,0,0.45)",
                    }}
                    data-testid="upcoming-card-elite-badge"
                  >
                    Elite
                  </div>
                )}
                <div className="aspect-[4/3] bg-[#FAFAFA] flex">
                  {hasAnyPhoto ? (
                    <>
                      <img src={leftPhoto} alt={r.dam_snapshot?.name || "Dam"} className="flex-1 object-cover" />
                      <img src={rightPhoto} alt={r.sire_snapshot?.name || "Sire"} className="flex-1 object-cover border-l border-white" />
                    </>
                  ) : (
                    // Photo-less placeholder — never just "NO PHOTO". Show
                    // the parents' names + breed in elegant typography
                    // so the card carries meaning while waiting for the
                    // breeder to upload a parent photo.
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-3 bg-gradient-to-br from-[#FBF7EC] to-[#F4E5B0]">
                      <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-[#8A6A00] mb-1">Planned pairing</div>
                      <div className="font-spectral text-[15px] leading-tight text-ink">
                        {r.dam_snapshot?.name || "Dam"}
                        <span className="text-[#C9A227] mx-1.5">×</span>
                        {r.sire_snapshot?.name || "Sire"}
                      </div>
                      <div className="text-[10px] text-[#6B6B6B] mt-1.5">{r.breed}</div>
                      <div className="text-[9.5px] text-[#9A9A9A] mt-2 italic">Parent photos coming soon</div>
                    </div>
                  )}
                </div>
                <div className="p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    {featured && (
                      <span className="text-[9px] uppercase tracking-[0.2em] font-bold bg-gold text-ink px-1.5 py-0.5 rounded">
                        Featured
                      </span>
                    )}
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-crimson">
                      {r.stage}
                    </span>
                  </div>
                  <div className="font-spectral text-lg leading-snug">
                    {r.dam_snapshot?.name} × {r.sire_snapshot?.name}
                  </div>
                  <div className="text-[12px] text-[#6B6B6B]">
                    {r.breed} · {DOLLARS(r.price_per_puppy_cents)} per pup
                  </div>
                  <div className="text-[11px] text-[#6B6B6B]">
                    Deposit {DOLLARS(r.deposit_amount_cents)} · go-home{" "}
                    {r.earliest_go_home_date || "TBD"}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
